import { useState } from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import { Button, Stack, Typography } from '@mui/material';
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';
import { z } from 'zod';

import { ButtonWithLoader } from '@/shared/ui/components/button/WithLoader';
import { ModalBase } from '@/shared/ui/components/modal/Base';
import { FormTextField } from '@/shared/ui/components/react-hook-form-fields/FormTextField';

import { useCustomAlerts } from '@/shared/lib/alert';

import { useSendFeedbackMutation } from '@/entities/feedback';

import { bakeAnnotations } from '@/features/report-problem/lib/bakeAnnotations';
import {
  fileToCanvas,
  MAX_SCREENSHOT_SIZE_BYTES,
  MAX_SCREENSHOT_SIZE_MB,
  SCREENSHOT_ACCEPT_TYPES
} from '@/features/report-problem/lib/fileToCanvas';
import { getFeedbackMetadata } from '@/features/report-problem/lib/getFeedbackMetadata';
import { INormalizedRect } from '@/features/report-problem/lib/rects';

import { ScreenshotAnnotator } from './ScreenshotAnnotator';

/** Максимальная длина комментария (совпадает с ожиданиями бэка по объёму текста) */
const MAX_COMMENT_LENGTH = 5000;

const reportProblemSchema = z.object({
  text: z
    .string()
    .trim()
    .min(1, 'Опишите проблему')
    .max(MAX_COMMENT_LENGTH, `Не более ${MAX_COMMENT_LENGTH} символов`)
});

type TReportProblemForm = z.infer<typeof reportProblemSchema>;

interface IProps {
  open: boolean;
  onClose: () => void;
  /** Снимок экрана; null — снять не удалось, форма работает без картинки */
  screenshot: HTMLCanvasElement | null;
  /** Снимок намеренно не снимали (страница поддержки) — это не ошибка */
  isCaptureSkipped?: boolean;
}

/**
 * Модальное окно «Сообщить о проблеме»: превью экрана с рамками-выделениями
 * и обязательный комментарий. Если снимка нет (не снялся, пропущен или удалён),
 * пользователь может приложить свою картинку и рисовать рамки по ней.
 * @param root0 - пропсы
 * @param root0.open - открыта ли модалка
 * @param root0.onClose - колбэк закрытия модалки
 * @param root0.screenshot - снимок текущего экрана
 * @param root0.isCaptureSkipped - снимок не снимали намеренно
 */
export const ReportProblemModal = ({
  open,
  onClose,
  screenshot,
  isCaptureSkipped
}: IProps) => {
  const [rects, setRects] = useState<INormalizedRect[]>([]);
  const [isScreenshotRemoved, setIsScreenshotRemoved] = useState(false);
  const [attachedScreenshot, setAttachedScreenshot] =
    useState<HTMLCanvasElement | null>(null);

  const methods = useForm<TReportProblemForm>({
    defaultValues: { text: '' },
    resolver: zodResolver(reportProblemSchema)
  });

  const {
    handleSubmit,
    formState: { isSubmitting }
  } = methods;

  const { mutate, isPending } = useSendFeedbackMutation();
  const { showCustomAlert } = useCustomAlerts();

  // Приложенная пользователем картинка приоритетнее автоснимка
  const effectiveScreenshot = attachedScreenshot ?? screenshot;
  const isScreenshotVisible = !!effectiveScreenshot && !isScreenshotRemoved;

  /** Подсказка в блоке без картинки: у «нет скриншота» три разные причины */
  const getHintText = () => {
    if (isScreenshotRemoved)
      return 'Скриншот удалён — отправим только описание';

    if (isCaptureSkipped) return 'Можно прикрепить скриншот проблемного места';

    return 'Не удалось сделать снимок экрана — можно прикрепить свой';
  };

  /** Сброс состояния модалки при закрытии: следующее открытие начинается с чистого листа */
  const handleClose = () => {
    methods.reset({ text: '' });
    setRects([]);
    setIsScreenshotRemoved(false);
    setAttachedScreenshot(null);
    onClose();
  };

  /** Удаление картинки: сбрасываем и автоснимок, и приложенный файл */
  const handleRemoveScreenshot = () => {
    setIsScreenshotRemoved(true);
    setAttachedScreenshot(null);
    setRects([]);
  };

  /**
   * Прикрепление своей картинки: проверяем размер и тип, затем декодируем в canvas
   * @param event - событие выбора файла
   */
  const handleAttach = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];

    // Один и тот же файл после ошибки можно выбрать снова только со сброшенным value
    event.target.value = '';

    if (!file) return;

    if (file.size > MAX_SCREENSHOT_SIZE_BYTES) {
      showCustomAlert(
        `Файл больше ${MAX_SCREENSHOT_SIZE_MB} МБ — выберите картинку меньшего размера`,
        {
          severity: 'warning'
        }
      );

      return;
    }

    const canvas = await fileToCanvas(file);

    if (!canvas) {
      showCustomAlert('Можно прикрепить только картинку PNG, JPEG или WebP', {
        severity: 'warning'
      });

      return;
    }

    setAttachedScreenshot(canvas);
    setIsScreenshotRemoved(false);
    setRects([]);
  };

  /**
   * Отправка сообщения: рамки запекаются в картинку прямо перед запросом
   * @param values - значения формы
   */
  const onSubmit: SubmitHandler<TReportProblemForm> = async values => {
    const screenshotFile =
      isScreenshotVisible && effectiveScreenshot
        ? await bakeAnnotations(effectiveScreenshot, rects)
        : null;

    // Тяжёлый скрин (4K-вьюпорт) бэк отклонил бы валидацией и потерял бы весь
    // фидбек — отправляем текст без картинки и честно предупреждаем.
    const isTooLarge =
      !!screenshotFile && screenshotFile.size > MAX_SCREENSHOT_SIZE_BYTES;

    if (isTooLarge) {
      showCustomAlert(
        'Скриншот получился слишком большим и не будет приложен',
        {
          severity: 'warning'
        }
      );
    }

    // Ключ screenshot кладём только с реальным файлом: undefined уехал бы в
    // FormData строкой "undefined" и бэк упал бы на разборе вложения
    mutate(
      {
        ...getFeedbackMetadata(),
        text: values.text,
        ...(screenshotFile && !isTooLarge ? { screenshot: screenshotFile } : {})
      },
      { onSuccess: handleClose }
    );
  };

  return (
    <ModalBase open={open} onClose={handleClose}>
      <FormProvider {...methods}>
        <Stack
          gap={6}
          width='100%'
          component='form'
          // noValidate: иначе браузер блокирует сабмит по required раньше zod
          // и вместо нашего текста ошибки показывает системную подсказку
          noValidate
          onSubmit={handleSubmit(onSubmit)}
        >
          <Typography variant='h6' sx={{ textAlign: 'center' }}>
            Сообщить о проблеме
          </Typography>

          {isScreenshotVisible && effectiveScreenshot ? (
            <Stack gap={2}>
              <ScreenshotAnnotator
                screenshot={effectiveScreenshot}
                rects={rects}
                onRectsChange={setRects}
              />

              <Typography variant='body/roboto/12' color='text.secondary'>
                Выделите проблемное место рамкой — просто проведите по снимку
              </Typography>

              <Stack direction='row' gap={2} flexWrap='wrap'>
                {!!rects.length && (
                  <Button variant='text' onClick={() => setRects([])}>
                    Очистить выделения
                  </Button>
                )}

                <Button
                  variant='text'
                  color='error'
                  onClick={handleRemoveScreenshot}
                >
                  Удалить скриншот
                </Button>
              </Stack>
            </Stack>
          ) : (
            <Stack gap={2} alignItems='flex-start'>
              <Typography variant='body/roboto/14' color='text.secondary'>
                {getHintText()}
              </Typography>

              <Button variant='outlined' component='label'>
                Прикрепить скриншот
                <input
                  hidden
                  type='file'
                  accept={SCREENSHOT_ACCEPT_TYPES.join(',')}
                  onChange={handleAttach}
                />
              </Button>
            </Stack>
          )}

          <FormTextField
            fieldName='text'
            label='Что произошло?'
            required
            multiline
            rows={4}
            isShowIcon={false}
            maxLength={MAX_COMMENT_LENGTH}
          />

          <ButtonWithLoader
            color='secondary'
            variant='contained'
            size='large'
            type='submit'
            isLoading={isPending || isSubmitting}
            sx={{ height: 50 }}
          >
            Отправить
          </ButtonWithLoader>
        </Stack>
      </FormProvider>
    </ModalBase>
  );
};
